import { locale } from './zendesk.utils';
import { config } from '../../config';
import { isMobileQuery, languageQuery } from '@gig/magic-common';
import type { Language } from '@gig/magic-sdk/dist/adapter/users/language';
import { magic } from '@gig/magic-sdk';
import { IZendeskService } from './zendesk.interface';
import { ZendeskBase } from './zendesk.base.class';
import type { IChatTokenParams } from '@gig/magic-sdk/dist/adapter/externalServices/chatToken';

declare global {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Window {
        zE: any;
        zEACLoaded: boolean;
    }
}

export class ZendeskChatOnly extends ZendeskBase implements IZendeskService {
    /**
     * Indicates if the chat has been loaded (script added to DOM, etc.).
     */
    isLoaded: boolean = false;
    /**
     * Indicates if loading above is in the process.
     */
    isLoading: boolean = false;
    /**
     * Functions executed when the initial chat loading begins.
     */
    private listeners = new Set<() => any>();
    private loginOnLoad: boolean = false;

    constructor() {
        super();
    }

    async preloadZendesk(): Promise<void> {
        if (this.isLoaded || this.isLoading) {
            return;
        }
        this.isLoading = true;
        try {
            await this.loadZendesk();
            this.isLoaded = true;
            if (this.loginOnLoad) {
                this.processLogin();
            }
        } catch (error) {
            console.error('Error preloading Zendesk Chat Only');
            console.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    login() {
        if (window.zE && this.isLoaded) {
            this.processLogin();
        } else {
            this.loginOnLoad = true;
        }
    }

    /**
     * Sets Zendesk's language to the current language.
     */
    async setLanguage(language: Language) {
        window?.zE?.('messenger:set', 'locale', locale(language));
        window?.zE?.setLocale?.(locale(language));
    }

    /**
     * Hides Zendesk by changing its z-index (underneath everything on mobile
     * devices and to level 0 on desktops).
     */
    async hide(): Promise<void> {
        const index = (await isMobileQuery()) ? -1 : 0;
        window?.zE?.('messenger:set', 'zIndex', index);
        window?.zE?.('messenger', 'hide');
    }

    /**
     * Shows Zendesk by moving it to the highest possible z-index.
     */
    show(): void {
        window?.zE?.('messenger:set', 'zIndex', 999999);
    }

    /**
     * Closes Zendesk chat, so only the button stays.
     */
    close(): void {
        window?.zE?.('messenger', 'close');
    }

    /**
     * Opens Zendesk.
     */
    open(): void {
        window?.zE?.('messenger', 'open');
    }

    /**
     * Lazily loads Zendesk's script and returns a promise that is resolved
     * when Zendesk's iframe's 'load' event is emitted.
     */
    loadZendesk(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const script = document.createElement(
                'script',
            ) as HTMLScriptElement;
            script.id = 'ze-snippet';
            script.src = config?.zendesk?.sdkUrl as string;
            script.onload = () => {
                const checker = setInterval(() => {
                    const iframe = document.querySelector(
                        'iframe[data-product="web_widget"]',
                    ) as HTMLIFrameElement | null;

                    if (iframe && window?.zEACLoaded) {
                        const onIframeLoad = () => {
                            iframe.removeEventListener('load', onIframeLoad);
                        };
                        iframe.addEventListener('load', onIframeLoad);
                        clearInterval(checker);
                        resolve();
                    }
                }, 4);
            };
            script.onerror = () => reject();
            document.body.appendChild(script);
        });
    }

    /**
     * A function that starts Zendesk and if it's not loaded, initiates lazy
     * loading. It also registers onClose handler.
     */
    async startingZendesk(): Promise<void> {
        if (this.isLoading) {
            return;
        }

        const language = await languageQuery();

        if (!window.zE) {
            try {
                this.isLoading = true;

                for (const listener of this.listeners) {
                    listener();
                }
                await this.loadZendesk();
                this.isLoaded = true;
            } catch (error) {
                console.error('Error loading Messenger');
                console.error(error);
            } finally {
                this.isLoading = false;
            }
        }

        this.setLanguage(language);
    }

    /**
     * Adds a listener to be fired when script loading starts.
     * @param fn A callback.
     * @returns function A function to remove the listener from the list.
     */
    onLoadingStart(fn: () => any): () => void {
        this.listeners.add(fn);

        return () => {
            this.listeners.delete(fn);
        };
    }

    private processLogin() {
        const userId = magic.session.getUser()?.userId;
        if (userId && this.isLoaded) {
            window.zE?.(
                'messenger',
                'loginUser',
                async (data: unknown) => {
                    const token =
                        await magic.methods.externalServices.getChatToken({
                            provider: 'zendesk',
                        } as IChatTokenParams);
                    (data as (token: string) => void)?.(token || '');
                    this.loginOnLoad = false;
                },
                (error: { [k: string]: unknown }) => {
                    if (error) {
                        console.error('Zendesk login Callback error', error);
                    }
                },
            );
        }
    }

    /**
     * Try to Pre-populates conversation fields with user's name and email.'
     * in fact we need to use logn() method fpr chat-only
     * @private
     */
    private prePopulateConversationFields() {
        const conversationFields: Array<{
            id: string;
            value: string | number | boolean;
        }> = [
            {
                id: 'firstName',
                value: magic.session.getUser()?.firstName || 'test',
            },
            {
                id: 'email',
                value: magic.session.getUser()?.firstName || 'test',
            },
        ];
        window?.zE?.('messenger:set', 'conversationFields', conversationFields);
    }
}
